import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { useDebounceCallback } from "usehooks-ts";
import { useAppStore } from "@/store";
import { RecordingStatus } from "@/modules/audio-recording/legacy";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { isWin } from "@/domains/platform/isWin";
import { windowModule } from "@/modules/window";
import { APP_CONFIG } from "@/domains/global/config";
import { MDSMenuContentItem, MDSMenuContentItemKind, type MDSMenuContentList } from "@/mds";
import { AudioCaptureActionsContextProvider } from "./audio-capture-actions.context";
import type {
  AudioCaptureActionsContextValue,
  AudioCaptureActionsProviderProps,
  AudioCaptureRecordingState,
  AudioCaptureUiState,
  AudioCaptureRefs,
} from "./audio-capture-actions.types";
import { createAudioCaptureSourceSnapshot } from "./audio-capture-actions.source";
import { RecallPermissionKind } from "@/modules/electron/types";
import { actions } from "@/actions";

export const AudioCaptureActionsProvider = observer(function AudioCaptureActionsProvider({
  children,
  noteId,
  onRequestClose,
  adapters,
}: PropsWithChildren<AudioCaptureActionsProviderProps>) {
  const { store: rootStore } = useAppStore();
  const store = adapters?.store ?? rootStore;
  const audioStore = store.audio;

  const [isTextTranscript, setIsTextTranscript] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const maxHistoryBars = 30;
  const [historyBarHeights, setHistoryBarHeights] = useState<number[]>(
    Array.from({ length: maxHistoryBars }, () => 0.0)
  );

  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const isAutoScrollEnabledRef = useRef(true);

  const sourceSnapshot = createAudioCaptureSourceSnapshot(store, { noteId });
  const {
    recording: recordingSnapshot,
    ui: uiSnapshot,
    environment,
    visualizer,
    recordingId,
  } = sourceSnapshot;

  const handleTranscriptScroll = useDebounceCallback(() => {
    if (!transcriptContainerRef.current) return;
    const { scrollTop, clientHeight, scrollHeight } = transcriptContainerRef.current;
    const isCloseToBottom = scrollTop + clientHeight >= scrollHeight - 10;
    isAutoScrollEnabledRef.current = isCloseToBottom;
  }, 30);

  useLayoutEffect(() => {
    if (
      isAutoScrollEnabledRef.current &&
      recordingSnapshot.transcript.hasTranscript &&
      transcriptContainerRef.current
    ) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [recordingSnapshot.transcript.fullText, recordingSnapshot.transcript.hasTranscript]);

  useEffect(() => {
    if (!visualizer) return;

    const timeDomainData = new Uint8Array(visualizer.fftSize);
    let frameCounter = 0;
    let animationFrameId: number;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      frameCounter += 1;
      if (frameCounter % 12 !== 0) return;

      const isRecording = recordingSnapshot.status === RecordingStatus.RECORDING;

      visualizer.getByteTimeDomainData(timeDomainData);

      if (isRecording) {
        let rms = 0;
        for (let i = 0; i < timeDomainData.length; i += 1) {
          const sample = (timeDomainData[i] - 128) / 128.0;
          rms += sample * sample;
        }
        rms = Math.sqrt(rms / timeDomainData.length);

        let barHeight = Math.log10(rms * 65 + 1) / Math.log10(66);
        barHeight = Math.max(barHeight, 0.1);
        barHeight = Math.min(barHeight, 1);

        setHistoryBarHeights(prev => {
          const newHeights = [...prev, barHeight];
          if (newHeights.length > maxHistoryBars) {
            return newHeights.slice(-maxHistoryBars);
          }
          return newHeights;
        });
      }
    };

    animate();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [recordingSnapshot.status, visualizer, maxHistoryBars]);

  const handleDeleteRecording = useCallback(() => {
    if (recordingId) {
      void audioStore.trashRecording({ audioRecordingId: recordingId });
    }
    setShowDeleteModal(false);
    onRequestClose?.();
  }, [audioStore, onRequestClose, recordingId]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    onRequestClose?.();
  }, [onRequestClose]);

  const handleOpenDeleteModal = useCallback(async () => {
    if (recordingSnapshot.status === RecordingStatus.RECORDING && recordingId) {
      await audioStore.pauseRecording({ audioRecordingId: recordingId });
    }
    setShowDeleteModal(true);
  }, [audioStore, recordingSnapshot.status, recordingId]);

  const handleManageSystemAudioSettings = useCallback(async () => {
    try {
      await electronModule.openSystemSoundSettings?.({ focus: "input" });
    } catch (error) {
      logger.warn({
        message: "[AudioCaptureActions] openSystemSoundSettings failed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }, []);

  const handleOpenVoiceModeFaqs = useCallback(() => {
    windowModule.openInNewTab({
      url: new URL(APP_CONFIG.URLS.FAQ_VOICE_MODE),
    });
  }, []);

  const dropdownContentList: MDSMenuContentList = useMemo(() => {
    const items: MDSMenuContentItem[] = [
      {
        id: "copy-transcript",
        kind: MDSMenuContentItemKind.Item,
        label: "Copy Transcript",
        onClick: () => {
          void actions.copyRecordingTranscript({
            recording: audioStore.activeRecording,
          });
        },
      },
      {
        id: "download-audio",
        kind: MDSMenuContentItemKind.Item,
        label: "Download Audio",
        onClick: () =>
          void actions.downloadRecordingAudio({ recording: audioStore.activeRecording }),
      },
    ];

    if (environment.isDesktop) {
      items.push({
        id: "separator",
        kind: MDSMenuContentItemKind.Divider,
      });
      items.push({
        id: "manage-system-audio",
        kind: MDSMenuContentItemKind.Item,
        label: "Manage System Audio Settings",
        onClick: () => {
          void handleManageSystemAudioSettings();
        },
      });

      items.push({
        id: "help-and-support",
        kind: MDSMenuContentItemKind.Item,
        label: "Voice Mode - FAQs",
        onClick: () => {
          void handleOpenVoiceModeFaqs();
        },
      });
    }

    return items;
  }, [
    audioStore.activeRecording,
    handleManageSystemAudioSettings,
    handleOpenVoiceModeFaqs,
    environment.isDesktop,
  ]);

  const shouldShowDropdown = dropdownContentList.length > 0;

  const waveform = useMemo(
    () => ({ bars: historyBarHeights, maxBars: maxHistoryBars }),
    [historyBarHeights, maxHistoryBars]
  );

  const recordingState: AudioCaptureRecordingState = {
    ...recordingSnapshot,
    waveform,
  };

  const uiState: AudioCaptureUiState = {
    ...uiSnapshot,
    dropdownItems: dropdownContentList,
    shouldShowDropdown,
    showDeleteModal,
    isTextTranscript,
  };

  const refs: AudioCaptureRefs = {
    transcriptContainerRef,
    isAutoScrollEnabledRef,
  };

  const downloadUrl = useMemo(() => {
    const target = isWin() ? APP_CONFIG.URLS.WINDOWS_APP : APP_CONFIG.URLS.MAC_APP;
    return new URL(target);
  }, []);

  const value: AudioCaptureActionsContextValue = {
    noteId,
    recording: recordingState,
    ui: uiState,
    environment,
    refs,
    actions: {
      recording: {
        start: () => {
          void audioStore.createAndStartAudioRecording({ associatedNoteId: noteId });
        },
        pause: () => {
          if (!recordingId) return;
          return audioStore.pauseRecording({ audioRecordingId: recordingId });
        },
        resume: () => {
          if (!recordingId) return;
          return audioStore.resumeRecording({ audioRecordingId: recordingId });
        },
        finish: () => {
          if (!recordingId) return Promise.resolve();
          return audioStore.finishRecording({ audioRecordingId: recordingId });
        },
        retryUpload: () => {
          if (!recordingId) return Promise.resolve();
          return audioStore.startUploading({ audioRecordingId: recordingId });
        },
        retryTransform: () => {
          if (!recordingId) return Promise.resolve();
          return audioStore.startTransforming({ audioRecordingId: recordingId });
        },
        clear: () => {
          if (!recordingId) return;
          void audioStore.trashRecording({ audioRecordingId: recordingId });
        },
      },
      deleteDialog: {
        open: handleOpenDeleteModal,
        close: handleCloseDeleteModal,
        confirm: handleDeleteRecording,
      },
      transcript: {
        toggleAdminMode: () => {
          setIsTextTranscript(prev => !prev);
        },
        handleScroll: handleTranscriptScroll,
      },
      autoStop: {
        setVisible: value => {
          audioStore.ui.setAutoStopModalVisible(value);
        },
      },
      systemAudio: {
        openSystemAudioSettings: handleManageSystemAudioSettings,
      },
      download: {
        openDesktopLink: () => {
          windowModule.openInNewTab({ url: downloadUrl });
        },
      },
      systemPermissions: {
        onOpenMicrophoneSystemPermissions: async () => {
          try {
            await electronModule.recall.openPermissionPane?.({
              permission: RecallPermissionKind.Microphone,
            });
          } catch (error) {
            logger.warn({
              message: "[AudioCaptureActions] onOpenMicrophoneSystemPermissions failed",
              info: { error: objectModule.safeErrorAsJson(error as Error) },
            });
          }
        },
      },
    },
    onRequestClose,
  };

  return (
    <AudioCaptureActionsContextProvider value={value}>
      {children}
    </AudioCaptureActionsContextProvider>
  );
});
